import React, { useState } from "react";
import { Bot } from "lucide-react";
import { Input, Textarea, Select, Checkbox, Label, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { TONES } from "./constants";
import { Field, StepShell } from "./_ui";
import type { StepProps } from "./types";

export function StepReceptionist({ state, setState, goNext, goBack }: StepProps) {
  const r = (state.receptionist ?? {}) as Record<string, unknown>;
  const bizName = (state.business_profile as Record<string, string>)?.name ?? "our team";
  const [form, setForm] = useState({
    name: (r.name as string) ?? "Ava",
    greeting: (r.greeting as string) ?? `Thanks for calling ${bizName}! How can I help you today?`,
    tone: (r.tone as string) ?? "friendly-professional",
    human_transfer_number: (r.human_transfer_number as string) ?? "",
    ai_disclosure: r.ai_disclosure != null ? !!r.ai_disclosure : true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.name.trim().length >= 1 && form.greeting.trim().length >= 1;

  const save = async () => {
    if (!valid) { toast.error("Give your receptionist a name and greeting."); return; }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(), greeting: form.greeting.trim(), tone: form.tone,
        ai_disclosure: form.ai_disclosure,
      };
      if (form.human_transfer_number.trim()) payload.human_transfer_number = form.human_transfer_number.trim();
      const next = await api.saveReceptionist(payload);
      setState(next);
      toast.success("Receptionist configured");
      goNext();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <StepShell
      icon={Bot}
      title="Meet your AI receptionist"
      description="This is who answers your calls. You can test and fine-tune it anytime."
      onBack={goBack}
      onContinue={save}
      saving={saving}
      continueDisabled={!valid}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Receptionist name" required testid="field-rcp-name">
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="rcp-name" />
        </Field>
        <Field label="Tone" testid="field-rcp-tone">
          <Select value={form.tone} onChange={(v) => set("tone", v)} options={TONES} testid="rcp-tone" />
        </Field>
      </div>
      <Field label="Greeting" required hint="The first thing callers hear" testid="field-rcp-greeting">
        <Textarea value={form.greeting} onChange={(e) => set("greeting", e.target.value)} rows={3} data-testid="rcp-greeting" />
      </Field>
      <Field label="Transfer to a human at" hint="Optional — where to send calls that need a person" testid="field-rcp-transfer">
        <Input value={form.human_transfer_number} onChange={(e) => set("human_transfer_number", e.target.value)}
          placeholder="+1 (312) 555-0123" data-testid="rcp-transfer" />
      </Field>
      <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="field-rcp-disclosure">
        <Checkbox checked={form.ai_disclosure} onChange={(v) => set("ai_disclosure", v)} testid="rcp-disclosure" />
        <span>
          <span className="block text-sm font-medium text-slate-800">Disclose that this is an AI assistant</span>
          <span className="block text-xs text-slate-500">Recommended — the receptionist will let callers know it's an AI.</span>
        </span>
      </label>
    </StepShell>
  );
}
